import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { makeClient } from "@/lib/api";
import { useTheme } from "@/context/theme";

const Ctx = createContext(null);

export function AuthProvider({ storagePrefix, authBase, children }) {
  const client = useMemo(() => makeClient(storagePrefix, authBase), [storagePrefix, authBase]);
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const { setTheme, onChange } = useTheme();

  useEffect(() => {
    if (!user) return undefined;
    return onChange((t) => client.patch(`${authBase}/me`, { theme: t }).catch(() => {}));
  }, [user, onChange, client, authBase]);

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem(`${storagePrefix}_token`);
    if (!token) {
      setChecking(false);
      return;
    }
    client
      .get(`${authBase}/me`)
      .then(({ data }) => {
        if (!active) return;
        setUser(data);
        if (data.theme) setTheme(data.theme, true);
      })
      .catch(() => {})
      .finally(() => active && setChecking(false));
    return () => {
      active = false;
    };
  }, [client, authBase, storagePrefix, setTheme]);

  const value = useMemo(() => {
    const storeTokens = (data) => {
      localStorage.setItem(`${storagePrefix}_token`, data.access_token);
      localStorage.setItem(`${storagePrefix}_refresh`, data.refresh_token);
      setUser(data.user);
      if (data.user?.theme) setTheme(data.user.theme, true);
    };
    return {
      user,
      checking,
      client,
      login: async (email, password) => (await client.post(`${authBase}/login`, { email, password })).data,
      verifyTotp: async (token, code, setup) => {
        const path = setup ? `${authBase}/totp/enable` : `${authBase}/totp/verify`;
        const { data } = await client.post(path, { token, code });
        storeTokens(data);
        return data;
      },
      updateMe: async (patch) => {
        const { data } = await client.patch(`${authBase}/me`, patch);
        setUser(data);
        return data;
      },
      logout: async () => {
        try {
          await client.post(`${authBase}/logout`);
        } catch (e) {
          /* ignore */
        }
        localStorage.removeItem(`${storagePrefix}_token`);
        localStorage.removeItem(`${storagePrefix}_refresh`);
        setUser(null);
      },
    };
  }, [user, checking, client, authBase, storagePrefix, setTheme]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
